import { z } from 'zod';
import { PostStatus } from '../enums';
import { LIMITS } from '../constants';
import { paginationQuerySchema, patchSchemaOf, seoSchema, slugSchema, sortDirectionSchema, uuidSchema, } from './common';
import { richTextDocSchema } from './rich-text';
const postBaseSchema = z.object({
    title: z.string().trim().min(LIMITS.title.min).max(LIMITS.title.max),
    /** Omit to derive from the title. Never silently changed once published. */
    slug: slugSchema.optional(),
    excerpt: z.string().trim().max(LIMITS.excerpt.max).nullish(),
    body: richTextDocSchema,
    coverMediaId: uuidSchema.nullish(),
    categoryId: uuidSchema.nullish(),
    tagIds: z.array(uuidSchema).max(12).default([]),
    status: z.enum(PostStatus).default(PostStatus.DRAFT),
    /**
     * The public kill switch, independent of editorial status. An admin can pull a
     * published post offline instantly and put it back without touching its
     * status or losing its original publish date.
     */
    isEnabled: z.boolean().default(true),
    /** Pinned posts sort above everything else in the public listing. */
    isPinned: z.boolean().default(false),
    /** Manual ordering within the listing. Lower sorts first. */
    sortOrder: z.number().int().min(0).default(0),
    /** Required when status is SCHEDULED; must be in the future. */
    scheduledAt: z.coerce.date().nullish(),
});
const postWritableSchema = postBaseSchema.extend(seoSchema.shape);
export const createPostSchema = postWritableSchema.refine((data) => data.status !== PostStatus.SCHEDULED || data.scheduledAt instanceof Date, { path: ['scheduledAt'], error: 'Pick a date and time to schedule this post for.' });
/**
 * Every field optional — the editor autosaves partial changes, so a PATCH must
 * not require the whole document round-tripped.
 *
 * The "SCHEDULED implies scheduledAt" invariant is therefore enforced in
 * PostsService rather than here: a PATCH may legitimately set `status` without
 * resending `scheduledAt`, in which case the value already on the row is what
 * has to be checked. A schema only sees the payload, not the row.
 */
export const updatePostSchema = patchSchemaOf(postWritableSchema.shape);
export const POST_SORT_FIELDS = [
    'publishedAt',
    'createdAt',
    'updatedAt',
    'title',
    'sortOrder',
    'viewCount',
];
/** Admin listing filters. */
export const adminPostQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(120).optional(),
    status: z.enum(PostStatus).optional(),
    categoryId: uuidSchema.optional(),
    tagId: uuidSchema.optional(),
    authorId: uuidSchema.optional(),
    isEnabled: z.coerce.boolean().optional(),
    sortBy: z.enum(POST_SORT_FIELDS).default('updatedAt'),
    sortDir: sortDirectionSchema,
});
/**
 * Public listing filters. Deliberately narrower than the admin query — no
 * `status`, no `authorId`, no `isEnabled`, because the public endpoint must
 * never be persuadable into returning unpublished content.
 */
export const publicPostQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(120).optional(),
    category: slugSchema.optional(),
    tag: slugSchema.optional(),
});
/** Status transition performed from the posts list without opening the editor. */
export const changePostStatusSchema = z.object({
    status: z.enum(PostStatus),
    scheduledAt: z.coerce.date().nullish(),
});
export const togglePostEnabledSchema = z.object({ isEnabled: z.boolean() });
//# sourceMappingURL=post.js.map